import React, { useCallback, useEffect, useMemo, useState } from 'react';
import usePageTitle from '../../hooks/usePageTitle';
import EmptyState from '../../components/EmptyState';
import { listPayers, upsertPayer } from '../../services/suiteApi';

const EMPTY_FORM = {
  payerId: '',
  payerName: '',
  adapterKey: 'unitedhealthcare',
  tradingPartnerServiceId: '',
  baseUrl: '',
  clientId: '',
  clientSecret: '',
  faxNumber: '',
  autoAppealOnDenial: false,
  supportsFax: true,
  enableMockCallbacks: false,
};

function payerToForm(p) {
  return {
    payerId: p.PayerId || p.payerId || '',
    payerName: p.PayerName || p.payerName || '',
    adapterKey: p.AdapterKey || p.adapterKey || 'unitedhealthcare',
    tradingPartnerServiceId: p.TradingPartnerServiceId || p.tradingPartnerServiceId || '',
    baseUrl: p.BaseUrl || p.baseUrl || '',
    clientId: p.ClientId || p.clientId || '',
    clientSecret: '',
    faxNumber: p.FaxNumber || p.faxNumber || '',
    autoAppealOnDenial: !!(p.AutoAppealOnDenial ?? p.autoAppealOnDenial),
    supportsFax: !!((p.Capabilities && p.Capabilities.supports_fax) ?? true),
    enableMockCallbacks: !!(p.Config && p.Config.enable_mock_callbacks),
  };
}

export default function PayerRosterPage() {
  usePageTitle('Payers');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [notice, setNotice] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await listPayers();
      const v = resp?.data;
      setItems(Array.isArray(v) ? v : (v?.items || []));
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const n = q.trim().toLowerCase();
    return items.filter((p) => {
      const hay = `${p.PayerName || ''} ${p.PayerCode || ''} ${p.PayerId || ''} ${p.AdapterKey || ''} ${p.TradingPartnerServiceId || ''}`
        .toLowerCase();
      return hay.includes(n);
    });
  }, [items, q]);

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setSaveError(null);
    setShowModal(true);
  };

  const openEdit = (p) => {
    setForm(payerToForm(p));
    setEditingId(p.PayerId || p.payerId);
    setSaveError(null);
    setShowModal(true);
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setSaveError(null);
  };

  const handleSave = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    setSaveError(null);
    try {
      const payerId = (form.payerId || '').trim();
      if (!payerId) {
        setSaveError('Payer ID is required.');
        setSaving(false);
        return;
      }
      await upsertPayer({
        payerId,
        payerName: form.payerName || undefined,
        adapterKey: form.adapterKey || undefined,
        tradingPartnerServiceId: form.tradingPartnerServiceId || undefined,
        baseUrl: form.baseUrl || undefined,
        clientId: form.clientId || undefined,
        clientSecret: form.clientSecret || undefined,
        faxNumber: form.faxNumber || undefined,
        autoAppealOnDenial: form.autoAppealOnDenial,
        capabilities: {
          supports_api: true,
          supports_fax: form.supportsFax,
          supports_appeal: true,
        },
        config: { enable_mock_callbacks: form.enableMockCallbacks },
      });
      setNotice(`Payer "${payerId}" ${editingId ? 'updated' : 'saved'}.`);
      setShowModal(false);
      setForm(EMPTY_FORM);
      setEditingId(null);
      await reload();
    } catch (err) {
      setSaveError(err?.response?.data?.message || err.message || 'Failed to save payer');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dm-page">
      <header className="dm-page__header">
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <h1 className="dm-page__title">Payers</h1>
            <p className="dm-page__lede">
              Configure payer accounts used by the auto-filing engine. Each
              payer maps to an adapter (UnitedHealthcare today, more next).
              Credentials are stored encrypted at rest.
            </p>
          </div>
          <button type="button" className="dm-btn dm-btn--primary" onClick={openAdd}>
            + Add payer
          </button>
        </div>
      </header>

      {error && <div className="dm-banner dm-banner--error">{error}</div>}
      {notice && <div className="dm-banner dm-banner--ok">{notice}</div>}

      <section className="dm-card">
        <div className="dm-card__title">
          <span>Configured payers ({filtered.length})</span>
          <input
            type="search"
            placeholder="Filter payers..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="dm-search"
          />
        </div>

        {loading ? (
          <div className="dm-card__empty">Loading...</div>
        ) : filtered.length === 0 ? (
          items.length === 0 ? (
            <EmptyState
              title="No payers in your roster yet"
              body="Click Add payer to configure your first one. Once you file claims, we will auto-roster any new payer the engine encounters."
              ctaLabel="Add payer"
              onCta={openAdd}
            />
          ) : (
            <div className="dm-card__empty">No payers match "{q}".</div>
          )
        ) : (
          <table className="dm-table">
            <thead>
              <tr>
                <th>Payer</th>
                <th>Adapter</th>
                <th>Payer ID (TPS)</th>
                <th>Base URL</th>
                <th>Auto-appeal</th>
                <th>Secret</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.PayerAccountId || p.PayerId || p.PayerCode || p.PayerName}>
                  <td>{p.PayerName || p.PayerId || '--'}</td>
                  <td>{p.AdapterKey || '--'}</td>
                  <td>{p.TradingPartnerServiceId || '--'}</td>
                  <td style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.BaseUrl || ''}>
                    {p.BaseUrl || '--'}
                  </td>
                  <td>{p.AutoAppealOnDenial ? 'Yes' : 'No'}</td>
                  <td>{p.HasSecret ? '••••' : '--'}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      type="button"
                      className="dm-btn dm-btn--secondary"
                      style={{ padding: '6px 12px', fontSize: 13 }}
                      onClick={() => openEdit(p)}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {showModal && (
        <div className="dm-modal-overlay" role="dialog" aria-modal="true" onClick={closeModal}>
          <div className="dm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dm-modal__header">
              <h2>{editingId ? `Edit payer "${editingId}"` : 'Add payer'}</h2>
              <button type="button" className="dm-modal__close" onClick={closeModal} aria-label="Close">×</button>
            </div>
            <form onSubmit={handleSave}>
              <div className="dm-modal__body">
                {saveError && <div className="dm-banner dm-banner--error">{saveError}</div>}
                <div className="dm-form-grid">
                  <div className="dm-form-field">
                    <label htmlFor="pf-id">Payer ID<span className="req">*</span></label>
                    <input
                      id="pf-id"
                      type="text"
                      value={form.payerId}
                      onChange={(e) => setField('payerId', e.target.value)}
                      required
                      maxLength={64}
                      placeholder="UHC"
                      readOnly={!!editingId}
                      style={editingId ? { background: 'var(--dm-bg)', color: 'var(--dm-muted)' } : undefined}
                    />
                  </div>
                  <div className="dm-form-field">
                    <label htmlFor="pf-name">Payer name</label>
                    <input id="pf-name" type="text" value={form.payerName} onChange={(e) => setField('payerName', e.target.value)} placeholder="UnitedHealthcare" />
                  </div>
                  <div className="dm-form-field">
                    <label htmlFor="pf-adapter">Adapter</label>
                    <select id="pf-adapter" value={form.adapterKey} onChange={(e) => setField('adapterKey', e.target.value)}>
                      <option value="unitedhealthcare">UnitedHealthcare (Optum)</option>
                    </select>
                  </div>
                  <div className="dm-form-field">
                    <label htmlFor="pf-tps">Trading Partner Service ID</label>
                    <input id="pf-tps" type="text" value={form.tradingPartnerServiceId} onChange={(e) => setField('tradingPartnerServiceId', e.target.value)} placeholder="87726" />
                  </div>
                  <div className="dm-form-field dm-form-field--full">
                    <label htmlFor="pf-base">Base URL</label>
                    <input id="pf-base" type="text" value={form.baseUrl} onChange={(e) => setField('baseUrl', e.target.value)} placeholder="https://payer.example.com" />
                  </div>
                  <div className="dm-form-field">
                    <label htmlFor="pf-clientid">Client ID</label>
                    <input id="pf-clientid" type="text" value={form.clientId} onChange={(e) => setField('clientId', e.target.value)} />
                  </div>
                  <div className="dm-form-field">
                    <label htmlFor="pf-secret">Client secret</label>
                    <input
                      id="pf-secret"
                      type="password"
                      value={form.clientSecret}
                      onChange={(e) => setField('clientSecret', e.target.value)}
                      placeholder={editingId ? 'leave blank to keep existing' : ''}
                    />
                  </div>
                  <div className="dm-form-field">
                    <label htmlFor="pf-fax">Fax number</label>
                    <input id="pf-fax" type="text" value={form.faxNumber} onChange={(e) => setField('faxNumber', e.target.value)} placeholder="18005551212" />
                  </div>
                </div>
                <div className="dm-form-toggles">
                  <label className="dm-form-toggle">
                    <input type="checkbox" checked={form.autoAppealOnDenial} onChange={(e) => setField('autoAppealOnDenial', e.target.checked)} />
                    Auto-appeal on denial
                  </label>
                  <label className="dm-form-toggle">
                    <input type="checkbox" checked={form.supportsFax} onChange={(e) => setField('supportsFax', e.target.checked)} />
                    Supports fax fallback
                  </label>
                  <label className="dm-form-toggle">
                    <input type="checkbox" checked={form.enableMockCallbacks} onChange={(e) => setField('enableMockCallbacks', e.target.checked)} />
                    Enable mock webhooks
                  </label>
                </div>
              </div>
              <div className="dm-modal__footer">
                <button type="button" className="dm-btn dm-btn--secondary" onClick={closeModal} disabled={saving}>Cancel</button>
                <button type="submit" className="dm-btn dm-btn--primary" disabled={saving}>
                  {saving ? 'Saving…' : (editingId ? 'Save changes' : 'Save payer')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
